import { CanonicalMeshType } from "../core/canonical_mesh.js";
import { IfcBooleanResult, IfcGeometricRepresentationItem } from "./ifc4_gen/index.js";
import { getMTLCleanName } from "../core/canonical_material.js";
/**
 * Geometry for an IFC model.
 */
export class IfcModelGeometry {
    /**
     * Construct this with an IFC step model.
     *
     * @param model The model this is from
     * @param isVoid
     */
    constructor(model, isVoid = false) {
        this.model = model;
        this.isVoid = isVoid;
        this.meshes_ = new Map();
    }
    /**
     * Get the number of items in this.
     *
     * @return {number}
     */
    get length() {
        return this.meshes_.size;
    }
    /**
     * Delete the temporaries from this.
     */
    deleteTemporaries() {
        for (const [key, value] of this.meshes_) {
            if (value.temporary) {
                this.meshes_.delete(key);
                if (value.type === CanonicalMeshType.BUFFER_GEOMETRY) {
                    value.geometry.delete();
                }
            }
        }
    }
    /**
     * Add a mesh to the geometry cache.
     *
     * @param mesh
     */
    add(mesh) {
        this.meshes_.set(mesh.localID, mesh);
    }
    /**
     * Drop the mesh for a particular local ID
     *
     * @param localID The local ID of the item to delete.
     */
    delete(localID) {
        const value = this.meshes_.get(localID);
        if (value !== void 0) {
            this.meshes_.delete(localID);
            if (value.type === CanonicalMeshType.BUFFER_GEOMETRY) {
                value.geometry.delete();
            }
        }
    }
    /**
     * Get an mesh by its matching local ID.
     *
     * @param localID
     * @return {CanonicalMesh | undefined}
     */
    getByLocalID(localID) {
        return this.meshes_.get(localID);
    }
    /**
     * Allows iteration through this.
     *
     * @return {IterableIterator}
     */
    [Symbol.iterator]() {
        return this.meshes_.values();
    }
    /**
     * Calculate the size of the geometry data in this.
     *
     * @return {number} - size of the geometry data
     */
    calculateGeometrySize() {
        let size = 0;
        for (const [_, mesh] of this.meshes_) {
            if (mesh.type === CanonicalMeshType.BUFFER_GEOMETRY) {
                const geometryObject = mesh.geometry;
                // using * 8 here because the points are being stored as doubles
                const pointsDataSize = geometryObject.GetVertexDataSize() * 8;
                const indexDataSize = geometryObject.GetIndexDataSize() * 4;
                size += pointsDataSize + indexDataSize;
            }
        }
        return size;
    }
    /**
     * Get the OBJs for all the curves in the cache (lazily)
     *
     * @yields {[IfcGeometricRepresentationItem, string]} Curves with their matching OBJ as a string
     */
    *objs() {
        const model = this.model;
        for (const [localID, mesh] of this.meshes_) {
            const geometryItem = model.getElementByLocalID(localID);
            if (!(geometryItem instanceof IfcGeometricRepresentationItem)) {
                continue;
            }
            if (mesh.type !== CanonicalMeshType.BUFFER_GEOMETRY) {
                continue;
            }
            let preamble = `# Mesh for ${geometryItem.toString()}\n`;
            if (geometryItem instanceof IfcBooleanResult) {
                const firstOperand = geometryItem.FirstOperand.toString();
                const secondOperand = geometryItem.SecondOperand.toString();
                preamble += `# IfcBooleanResult ${firstOperand} ${secondOperand}\n`;
            }
            const materials = this.isVoid ? this.model.voidMaterials : this.model.materials;
            const geometryMaterial = materials.getMaterialByGeometryID(localID);
            if (geometryMaterial !== void 0) {
                const materialObject = this.model.getElementByLocalID(geometryMaterial[1]);
                if (materialObject !== void 0) {
                    const materialName = `${geometryItem.expressID}_${materialObject.expressID}.mtl`;
                    preamble += `mtllib ${materialName}\n`;
                    preamble += `usemtl ${getMTLCleanName(geometryMaterial[0])}\n`;
                    const objFileContents = mesh.geometry.dumpToOBJ(preamble);
                    yield [geometryItem, objFileContents, geometryMaterial[0], materialName];
                }
            }
            const objFileContents = mesh.geometry.dumpToOBJ(preamble);
            yield [geometryItem, objFileContents, undefined, undefined];
        }
    }
}
